import React, { useState, useEffect } from "react"
import useTitle from "@/hooks/useTitle"
import EtBtnFormBasic from "@/components/common/buttons/EtBtnFormBasic"
import PulseLoader from "react-spinners/PulseLoader"
import EtContentEditorLightWithPlace from "@/utils/EtContentEditorLightWithPlace"
import { formatDateISO } from "@/utils/formatDate"
import { useGetActionTypesQuery } from "../actionTypes/actionTypesApiSlice"
import { useUpdateUserActionMutation } from "./userActionsApiSlice"
import {
  PlusIcon,
  MinusIcon,
} from "@heroicons/react/24/outline"

// 08 Sep 2025
import { useGetUsersQuery } from "@/features/users/usersApiSlice"
import useAuth from '@/hooks/useAuth'

// 01 Sep 2025
import { convertLocalDateToUTCISO, formatUTCToLocalDateInput } from '@/utils/dateUtils'
import moment from 'moment';

// 03 Sep 2025
import { getCurrentRecurrenceLogEntry } from "@/utils/logUtils";

const EtUpdateUserActionComp = ({
  userAction = null,
  id=null,
  onClose = () => {},
  refetchUserActions = () => {}
}) => {

  // First Created - 04 Apr 2025
  // Author - Atul Pandey
  // Inherited from EtUpdateUserAction
  // Updated - 10 Jun 2025 - provided alternate data states for setting the form data based on incoming data in userAction prop
  // Updated - 31 Aug 2025 - refined for updating Action Goals and recurrence
  // Updated - 01 Sep 2025 - added logic for updating recurrence patterns
  // Updated - 03 Sep 2025 - added logic for updating actionUoM
  // Updated - 03 Sep 2025 - added logic for updating recurring action logs
  // Updated - 04 Sep 2025 - added logic for updating targetValue
  // Updated - 08 Sep 2025 - alert for empty progress note for recurrence log updates
  // Updated - 08 Sep 2025 - changes for assignedUsers updates
  // Updated - 09 Sep 2025 - changes for assignedUsers updates
  // Updated - 19 Sep 2025 - display userAction goal role - Enabler, Outcome
  // Updated - 01 Dec 2025 - time zone fix
  // Updated - 07 Dec 2025 - user time zone fix
  // Updated - 11 Dec 2025 - alert on update of headers without changing the logs fix - handleSubmit
  // Updated - 11 Dec 2025 — Prevent recurrenceEndDate from exceeding completeBy
  // Updated - 12 Dec 2025 — Assigned to users based on user's choice
  // Updated - 12 Dec 2025 — Users assigned at creation CANNOT be removed; new ones CAN toggle
  // Updated - 16 Dec 2025 — Commented / Muted progress logging through this comp. It will now be managed via the display comp's progress logging - i.e. EtUserSessionActionCard comp


  // 0 - Set Page Title
useTitle('Update A User Action')

// console.log('incoming user action:', userAction) // 31 Aug 2025 - Debug

// 1a-  Auth and Local User Info - 08 Sep 2025
  const { username } = useAuth()
  // console.log('username', username)

// 1b - State variables
const [progressNoteText, setProgressNoteText] = useState(""); // Input for action note text
const [progressValue, setProgressValue] = useState(userAction?.userActionProgress); // Slider and input box sync
// const [showMainSection, setShowMainSection] = useState(false); // for collapsible header
const [showMainSection, setShowMainSection] = useState(true); // 16 Dec 2025

// 08 Sep 2025 - Assigned Users
  const [assignedUsers, setAssignedUsers] = useState(userAction?.assignedUsers || []); 
  const [assignmentType, setAssignmentType] = useState(userAction?.assignmentType || "Collective");

  const assignedUserIds = assignedUsers.map((u) => u.userId); // 🔸 for badge rendering

// 12 Dec 2025 - NEW toggle for showing/hiding assignment section
const [showAssignUI, setShowAssignUI] = useState(false);
 

// 2 - Define the mutation to update the user action
const [updateUserAction, { isLoading, isSuccess, isError, error }] = useUpdateUserActionMutation()

const { data: usersData } = useGetUsersQuery(); // 08 Sep 2025

const extractFilteredUserEntitiesFromAdapter = (dataAdapter) => {
    if (!dataAdapter || !dataAdapter.ids || !dataAdapter.entities) return [];
    return dataAdapter.ids.map((id) => dataAdapter.entities[id])
    ?.filter(
        (item) => item?.username === username
        );
  };

const extractEntitiesFromAdapter = (dataAdapter) => {
    if (!dataAdapter || !dataAdapter.ids || !dataAdapter.entities) return [];
    return dataAdapter.ids.map((id) => dataAdapter.entities[id])
  };

  const extractFilteredEntitiesFromAdapter = (dataAdapter) => {
    if (!dataAdapter || !dataAdapter.ids || !dataAdapter.entities) return [];
    return dataAdapter.ids.map((id) => dataAdapter.entities[id])
    ?.filter(
        // (item) => item.userId?._id === userId && item?.userOrgId === userOrgId
        (item) => item.userId?._id === userId
        );
  };

  const userInfo = extractFilteredUserEntitiesFromAdapter(usersData)
  const users = extractEntitiesFromAdapter(usersData)
  
  // 4️⃣ Extract userId and userOrgId from current user
  const userId = userInfo[0]?._id || null
  const userOrgId = userInfo[0]?.userOrgId?._id || null
  const userRoles = userInfo[0]?.roles || []      // 08 Sep 2025

  // console.log('userId', userId)
  // console.log('userOrgId', userOrgId)
  // console.log('users', users)
  // console.log('userInfo', userInfo)
  // console.log('userRoles', userRoles)

// 3 - Get action types data for options
const { actionTypes } = useGetActionTypesQuery('actionTypesList', {
  selectFromResult: ( { data }) => ({
    actionTypes: data?.ids.map(id => data?.entities[id])
  })
})

//console.log('action types', actionTypes)

// 4 - Create options to select a session type from list of session types
const actionTypeOptions = actionTypes ? [
  <option key="placeholder" value="" disabled selected>
    Select a Action Type
  </option>,
  ...actionTypes?.map(actionType => (
    <option key={actionType._id} value={actionType._id}>
      {actionType.actionTypeName}
    </option>
  ))
  ] : []

// 5a - Selected focus & objects:

let selectedFocus = null;

if (userAction.actionVisionClientIds?.length > 0) selectedFocus = "Client";
else if (userAction.actionVisionOrgIds?.length > 0) selectedFocus = "Org";
else if (userAction.actionVisionPartnerIds?.length > 0) selectedFocus = "Partner";
else if (userAction.actionVisionTeamIds?.length > 0) selectedFocus = "Team";
else if (userAction.actionVisionSelfIds?.length > 0) selectedFocus = "Self";

const visionObj = userAction[`actionVision${selectedFocus}Ids`]?.[0]?.visionId;
const missionObj = userAction[`actionMission${selectedFocus}Ids`]?.[0]?.missionId;
const planObj = userAction[`actionPlan${selectedFocus}Ids`]?.[0]?.planId;

const visionName = visionObj?.sharedVisionId?.visionName || visionObj?.visionName || "";
const missionName = missionObj?.sharedMissionId?.missionName || missionObj?.missionName || "";
const planName = planObj?.planName || "";

const goalEntry = userAction?.connectedMissionGoals?.[0];

const goalName = goalEntry?.connectedSharedGoalId ?
  goalEntry?.connectedSharedGoalId?.goalName :
  goalEntry?.connectedMissionGoalId?.goalName || "";

// 19 Sep 2025 - GoalPeriod Name
const goalPeriodName = goalEntry?.connectedSharedGoalId
  ? goalEntry?.connectedSharedGoalId?.goalBreakdown?.find(
      p => p._id === goalEntry.connectedGoalBreakdownId
    )?.period
  : goalEntry?.connectedMissionGoalId?.goalBreakdown?.find(
      p => p._id === goalEntry.connectedGoalBreakdownId
    )?.period || "";

// 5b - Get the current Log to be updated - 03 Sep 2025
// let currentLog;

// if(userAction?.recurringActionLog) {
// currentLog = getCurrentRecurrenceLogEntry(userAction);}

const currentLog = getCurrentRecurrenceLogEntry(userAction)

// 5c - Fill the form data with extracted session info
const [formData, setFormData] = useState({
    userId: userAction?.userId?._id || userAction?.userId,                   // 10 Jun 2025 - added userAction.userId
    trailId: userAction?.trailId?._id || userAction?.trailId,                // 10 Jun 2025 - added userAction.trailId
    sessionId: userAction?.sessionId?._id || userAction?.sessionId,          // 10 Jun 2025 - added userAction.sessionId
    actionTypeId:userAction?.actionTypeId?._id || userAction?.actionTypeId,  // 10 Jun 2025 - added userAction.actionTypeId
    userActionName:userAction?.userActionName,
    userActionDesc:userAction?.userActionDesc,
    // completeBy:userAction?.completeBy, 
    completeBy:formatUTCToLocalDateInput(userAction.completeBy), // 01 Sep 2025, 01 Dec 2025, uncommented
    aboutMe:userAction?.aboutMe,

    // 31 Aug 2025
    trailName: userAction?.trailId?.trailName || "No Trail Name",
    selectedFocus: selectedFocus,
    visionName: visionName,
    missionName: missionName,
    planName: planName,
    goalName: goalName,
    goalPeriodName: goalPeriodName, // 19 Sep 2025

    // 01 Sep 2025
    actionRecurrencePattern: userAction?.actionRecurrencePattern || "OneTime",
    // recurrenceStartDate: userAction?.recurrenceStartDate || "",
    // recurrenceEndDate: userAction?.recurrenceEndDate || "",
    // 01 Dec 2025
    recurrenceStartDate: userAction?.recurrenceStartDate
    ? formatUTCToLocalDateInput(userAction.recurrenceStartDate)
    : "",

    recurrenceEndDate: userAction?.recurrenceEndDate
      ? formatUTCToLocalDateInput(userAction.recurrenceEndDate)
      : "",

    weekdayOnly: userAction?.weekdayOnly || false,

    // 03 Sep 2025
    actionUoM: userAction?.actionUoM || "",
    
    // 04 Sep 2025
    targetValue: userAction?.targetValue || 0,
    actualValue: userAction?.actualValue || 0, // for the action level

    // 04 Sep 2025 - Recurring log inputs
    todayProgress: currentLog?.progress || 0,
    // todayProgressNote: currentLog?.progressNote || "",
    todayProgressNote: "", // 11 Dec 2025 - to avoid loading an existing note for a new recurring log
    todayActualValue: currentLog?.actualValue || 0,

    // 08 Sep 2025 - Assigned User changes
    assignedUsers: assignedUsers,
    assignmentType: assignmentType,

    // 19 Sep 2025 - action Goal Role - Enabler or Outcome
    actionGoalRole: userAction?.actionGoalRole || "Enabler",
})
// console.log('Complete by:', formData.completeBy)

// 5d - 19 Sep 2025 Action's Target Value alignment to Goal or Goal Period if the action is an Outcome
useEffect(() => {
  const goalEntry = userAction?.connectedMissionGoals?.[0];

  let target = null;
  let uom = null;

  // if (!goalEntry || formData.actionGoalRole !== "Outcome") return;
  if (formData.actionGoalRole !== "Outcome") return;

  const focus = formData.selectedFocus;

  // 21 Sep 2025 - Refined the logic for mission linked actions

  if (goalEntry) {
  // ✅ Goal alignment (existing logic)
    if (["Client", "Org", "Partner"].includes(focus)) {
      target = goalEntry?.connectedSharedGoalId?.goalTargetValue;
      uom = goalEntry?.connectedSharedGoalId?.goalUoM;
      if (goalEntry?.connectedGoalBreakdownId) {
        const matchingPeriod = goalEntry?.connectedSharedGoalId?.goalBreakdown?.find(
          p => p._id === goalEntry.connectedGoalBreakdownId
        );
        if (matchingPeriod?.targetValue !== undefined) {
          target = matchingPeriod.targetValue;
        }
      }
    } else {
      target = goalEntry?.connectedMissionGoalId?.goalTargetValue;
      uom = goalEntry?.connectedMissionGoalId?.goalUoM;
      if (goalEntry?.connectedGoalBreakdownId) {
        const matchingPeriod = goalEntry?.connectedMissionGoalId?.goalBreakdown?.find(
          p => p._id === goalEntry.connectedGoalBreakdownId
        );
        if (matchingPeriod?.targetValue !== undefined) {
          target = matchingPeriod.targetValue;
        }
      }
    }
  } else {            // 21 Sep 2025
    // ✅ 21 Sep 2025 Mission fallback logic
    // const missionId = formData?.[`actionMission${focus}Ids`]?.[0]?.missionId;

    // const missionList = (() => {
    //   switch (focus) {
    //     case 'Self': return userMissionSelfs;
    //     case 'Client': return userMissionClients;
    //     case 'Org': return userMissionOrgs;
    //     case 'Team': return userMissionTeams;
    //     case 'Partner': return userMissionPartners;
    //     default: return [];
    //   }
    // })();

    // const mission = missionList?.find(m => m._id === missionId);
    // const missionObj = ["Client", "Org", "Partner"].includes(focus)
    //   ? mission?.sharedMissionId
    //   : mission;

    // target = missionObj?.mission1YrTargetValue;
    // uom = missionObj?.missionUoMType?.missionMetricUoMTypeName;

    const missionEntry = userAction?.[`actionMission${focus}Ids`]?.[0];
    const missionIdObj = missionEntry?.missionId;

    const isSharedFocus = ["Client", "Org", "Partner"].includes(focus);
    const missionObj = isSharedFocus ? missionIdObj?.sharedMissionId : missionIdObj;

    target = missionObj?.mission1YrTargetValue;
    uom = missionObj?.missionUoMType?.missionMetricUoMTypeName;

  }

  const needsTargetUpdate =
    (formData.targetValue === 0 || Number(formData.targetValue) !== Number(target));
  const needsUomUpdate =
    (!formData.actionUoM || formData.actionUoM !== uom);

  if (needsTargetUpdate || needsUomUpdate) {
    const confirm = window.confirm(
      "This action is marked as an Outcome, but its target value or UoM doesn't match the goal. Do you want to align it?"
    );
    if (confirm) {
      setFormData(prev => ({
        ...prev,
        // targetValue: Number(target),
        // actionUoM: uom 
        targetValue: Number(target) || 0,
        actionUoM: uom || ""
      }));
    }
  }

}, [formData.actionGoalRole, formData.targetValue, formData.actionUoM, formData.selectedFocus, userAction]);


// 5e - Check for allowing recurrence start and end dates - 01 Sep 2025, Updated - 04 Sep 2025 (let instead of const)
let allowChangeStart = true;
let allowChangeEnd = true;
let allowToggleWeekdayOnly = false;

if (formData.actionRecurrencePattern !== "OneTime" && Array.isArray(formData.recurringActionLog)) {


  //07 Dec 2025
  const userTZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // normalize everything to user's timezone start-of-day
  const today = moment().tz(userTZ).startOf('day');
  const start = moment.tz(formData.recurrenceStartDate, "YYYY-MM-DD", userTZ).startOf('day');
  const end = moment.tz(formData.recurrenceEndDate, "YYYY-MM-DD", userTZ).startOf('day');

  // const today = moment().startOf('day');
  // const start = moment(formData.recurrenceStartDate).startOf('day');
  // const end = moment(formData.recurrenceEndDate).startOf('day');

  const anyLogsLogged = formData.recurringActionLog.some(log => log.logged);
  const hasLogOnEndDate = formData.recurringActionLog.some(log =>
    moment(log.logDate).tz(userTZ).startOf('day').isSame(end)
  );
  
  // const hasLogOnEndDate = formData.recurringActionLog.some(log =>
  //   moment(log.logDate).startOf('day').isSame(end)
  // );

  allowChangeStart = start.isSameOrAfter(today) && !anyLogsLogged;
  allowChangeEnd = end.isSameOrAfter(today) && !hasLogOnEndDate;
  allowToggleWeekdayOnly = formData.actionRecurrencePattern === "Daily" && allowChangeStart && allowChangeEnd;
}

// 6 - Reset form upon successful submission
useEffect(() => {
  if (isSuccess) {
    // Clear form
    setFormData({
      userId: "",
      trailId: "", 
      sessionId: "",
      actionTypeId: "",
      userActionName: "",
      userActionDesc: "",
      completeBy: "", 
      aboutMe: "",
      pic: "" ,
      // 01 Sep 2025
      actionRecurrencePattern: "",
      recurrenceStartDate: "",
      recurrenceEndDate: "",
      weekdayOnly: false,
      //03 Sep 2025
      actionUoM: "",
      //04 Sep 2025
      targetValue: 0,
    })

    // Refetch updated actions and close modal
    if (refetchUserActions) refetchUserActions()
    if (onClose) onClose()
  }
}, [isSuccess, refetchUserActions, onClose])

// 7 - Form field updates / change handling - changed for date handling - 01 Sep 2025
const handleChange = (e) => {
  const { name, value, files } = e.target;

  if (name === "pic") {
    setFormData(prevState => ({ ...prevState, pic: files[0] }));
  } 
  // ✅ Handle date fields: convert local date string to UTC ISO string
  else if (name === "recurrenceStartDate" || name === "recurrenceEndDate") {
    // const utcDate = moment(value, "YYYY-MM-DD").startOf("day").toISOString(); // 01 Dec 2025 - commented
    // const utcDate = convertLocalDateToUTCISO(value); // 01 Dec 2025
    // setFormData(prevState => ({ ...prevState, [name]: utcDate }));
    // 🟠 07 Dec 2025 - Store raw YYYY-MM-DD for recurrence dates; convert to UTC only on submit
    setFormData(prevState => ({ ...prevState, [name]: value })); // 07 Dec 2025
  } 
  else {
    setFormData(prevState => ({ ...prevState, [name]: value }));
  }
};

// 8 - Form Submit Logic
const handleSubmit = async (e) => {
  e.preventDefault()

  // console.log("Recurrence pattern:", formData.actionRecurrencePattern);
  // console.log("Original action recurrencePattern:", userAction.actionRecurrencePattern);

  // 11 Dec 2025 - ✅ FIX – Only validate this for OneTime actions

  let noteChanged = false;
  let progressChanged = false; 

  if (formData.actionRecurrencePattern === "OneTime") {
    noteChanged = progressNoteText.trim().length > 0;
    progressChanged = progressValue !== userAction?.userActionProgress;

    if (progressChanged && !noteChanged) {
      alert("Please add a note if you are updating the progress value.");
      return;
    }
  }

  const newNote = {
    actionNoteText: progressNoteText,
    actionNoteDate: new Date().toISOString(),
    actionNoteProgress: progressValue,
    actionNoteActualValue: Number(formData.actualValue), // ✅ 04 Sep 2025
  };

  // 🚫 11 Dec 2025 — Prevent recurrenceEndDate from exceeding completeBy
  if (
    formData.actionRecurrencePattern !== "OneTime" &&
    formData.recurrenceEndDate &&
    formData.completeBy
  ) {
    const endDate = moment(formData.recurrenceEndDate).startOf("day");
    const completeBy = moment(formData.completeBy).startOf("day");

    if (endDate.isAfter(completeBy)) {
      alert("Recurrence End Date cannot be after the Action's Complete By date.");
      return;
    }
  }


  // Initialize an object to collect the form data
  const actionData = {
      userId: formData.userId,
      trailId: formData.trailId, 
      sessionId: formData.sessionId,
      actionTypeId:formData.actionTypeId,
      userActionName:formData.userActionName,
      userActionDesc:formData.userActionDesc,
      // completeBy:formData.completeBy,
      completeBy:convertLocalDateToUTCISO(formData.completeBy), // 01 Sep 2025
      // userActionProgress: progressValue,                     // 04 Sep 2025 - moved in userRecurrencePattern sections
      // userActionNotes: [newNote], // ✅ Send only the new note (backend handles appending) 
      
      // ⭐ 07 Dec 2025 - NEW — preserve original action timezone if it exists
      userTimeZone: userAction?.userTimeZone 
      ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
      
      
      aboutMe:formData.aboutMe,  
      
      // ✅ Recurrence-related fields - 01 Sep 2025
      actionRecurrencePattern: formData.actionRecurrencePattern,
      // 01 Dec 2025
      // recurrenceStartDate: formData.recurrenceStartDate,
      // recurrenceEndDate: formData.recurrenceEndDate,
      recurrenceStartDate: convertLocalDateToUTCISO(formData.recurrenceStartDate),
      recurrenceEndDate: convertLocalDateToUTCISO(formData.recurrenceEndDate),

      weekdayOnly: formData.weekdayOnly,

      // 03 Sep 2025
      actionUoM: formData.actionUoM,  
      
      // 04 Sep 2025
      targetValue: formData.targetValue,  

      // 08 Sep 2025
      // assignedUsers: formData.assignedUsers,
      // assignmentType: formData.assignmentType,
  }

  // 09 Sep 2025 - Build assignedUsers with current assignmentType
  if (userRoles.includes("OrgSponsor")) {
    actionData.assignedUsers = assignedUsers.map(user => ({
      userId: user.userId,
      assignmentType: user.assignmentType || assignmentType,
    }));
  }

  // 03 Sep 2025
  if (formData.actionRecurrencePattern !== "OneTime" && currentLog) {


    // 11 Dec 2025 - fix - ✅ Determine if a log update is truly needed

    const logProgressChanged =
      Number(formData.todayProgress ?? 0) !== Number(currentLog.progress ?? 0);

    const logActualChanged =
      Number(formData.todayActualValue ?? 0) !== Number(currentLog.actualValue ?? 0);

    const noteEntered = formData.todayProgressNote?.trim() !== "";

    const shouldSendLogUpdate =
     logProgressChanged || logActualChanged || noteEntered;

    console.log("💬 Recurrence Log — shouldSendLogUpdate:", shouldSendLogUpdate);
    console.log("   progressChanged:", logProgressChanged);
    console.log("   actualChanged:", logActualChanged);
    console.log("   noteEntered:", noteEntered);

   // ⭐ NEW GUARD — If nothing changed, skip the entire recurrence log update
   if (!shouldSendLogUpdate) {
     console.log("✨ Header-only update — skipping recurrence log update.");
     // Do NOT set recurringLogDate or any log fields
   } else {

     // ⛔ 11 Dec 2025 - Enforce note if numeric values changed
      if ((logProgressChanged || logActualChanged) && !noteEntered) {
        alert("Please add a note before submitting progress or actual value updates.");
        return;
      }

    // // ⚠️ Require note ONLY if log progress OR actual value changed
    // if ((logProgressChanged || logActualChanged) &&
    //     (!formData.todayProgressNote || formData.todayProgressNote.trim() === "")) {

    //   alert("Please add a note before submitting progress or actual value updates.");
    //   return;
    // }

    
    // 🚫 08 Sep 2025 - Prevent empty progress note submission
    // if (!formData.todayProgressNote || formData.todayProgressNote.trim() === "") {
    //   alert("Please add a note before submitting progress for this recurrence log.");
    //   return;
    // }


    // 1️⃣ Set log-specific metadata

    // 07 Dec 2025
    // STEP 4 — Normalize recurring log date to user timezone before sending
    const userTZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // 08 Dec 2025 - Since backed is now timezone compliant
    actionData.recurringLogDate = currentLog.logDate;


    // actionData.recurringLogDate = moment(currentLog.logDate)
    //   .tz(userTZ)                // interpret in user timezone
    //   .startOf('day')            // normalize to start of local day
    //   .utc()                     // convert back to UTC for backend
    //   .toISOString();

    // actionData.recurringLogDate = moment(currentLog.logDate).toISOString();


    // actionData.actualValue = formData.todayActualValue;
    actionData.recurringActualValue = formData.todayActualValue; // 04 Sep 2025 - to avoid name collision from the action level actualValue
    
    // 11 Dec 2025 - Fix:
    if (noteEntered) {
    // if (logProgressChanged || logActualChanged || formData.todayProgressNote.trim() !== "") {
      actionData.progressNote = formData.todayProgressNote.trim();
    }
    
    // actionData.progressNote = formData.todayProgressNote;
    
    actionData.progress = formData.todayProgress;

    // 2️⃣ Determine total number of logs and weight per log
    const totalLogs = userAction?.recurringActionLog?.length || 1;
    const logWeight = 100 / totalLogs;

    // 3️⃣ Compute scaled contribution of current log (updated value)
    const currentProgress = formData.todayProgress || 0;
    const scaledProgress = (currentProgress / 100) * logWeight;

    // 4️⃣ Create and assign recurring progress note
    const recurringNote = {
      actionNoteText: formData.todayProgressNote,
      actionNoteDate: new Date().toISOString(),
      actionNoteProgress: Math.round(scaledProgress),
      actionNoteActualValue: Number(formData.todayActualValue), // ✅ Include actual value in note
    };

    // 11 Dec 2025 - fix:
    // Only create a note when something actually changed
    // if (logProgressChanged || logActualChanged || formData.todayProgressNote.trim() !== "") {

    // ✅ Only create a note when user actually entered one
    if (noteEntered) {
      actionData.userActionNotes = [recurringNote];
    }
    // actionData.userActionNotes = [recurringNote];

    // 5️⃣ Sum scaled progress across past logs (excluding current log)

    // 07 Dec 2025
    const currentLogDate = moment(currentLog.logDate)
    .tz(userTZ)
    .startOf("day")
    .format("YYYY-MM-DD");
    // const currentLogDate = moment(currentLog.logDate).startOf("day").format("YYYY-MM-DD");
    
    
    let totalScaledProgress = 0;
    (userAction?.recurringActionLog || []).forEach(log => {
      // 07 Dec 2025
      const logDate = moment(log.logDate)
      .tz(userTZ)
      .startOf("day")
      .format("YYYY-MM-DD");
      // const logDate = moment(log.logDate).startOf("day").format("YYYY-MM-DD");
      if (logDate !== currentLogDate) {
        const progress = Number(log.progress || 0);
        totalScaledProgress += (progress / 100) * logWeight;
      }
    });

    // 6️⃣ Add current log's updated contribution
    totalScaledProgress += scaledProgress;

    // 7️⃣ Round and assign final cumulative progress
    actionData.userActionProgress = Math.round(totalScaledProgress);

    // 8️⃣ Compute cumulative actualValue across logs (excluding current log)
    let cumulativeActualValue = 0;
    (userAction?.recurringActionLog || []).forEach(log => {

      // 08 Dec 2025
      const logDate = moment(log.logDate)
      .tz(userTZ)
      .startOf("day")
      .format("YYYY-MM-DD");

      // const logDate = moment(log.logDate).startOf("day").format("YYYY-MM-DD");


      if (logDate !== currentLogDate) {
        cumulativeActualValue += Number(log.actualValue || 0);
      }
    });

    // 9️⃣ Add current actualValue to cumulative
    cumulativeActualValue += Number(formData.todayActualValue || 0);

    // 🔟 Assign rolled-up actual value to action level
    actionData.actualValue = cumulativeActualValue;
     } // ⭐ END shouldSendLogUpdate guard
 } // END recurrence-pattern check

  // ✅ Only include the new note if either value or note is changed
  // ✅ And only for OneTime actions (non-recurring) - 04 Sep 2025
  if (
      formData.actionRecurrencePattern === "OneTime" && // 04 Sep 2025
      (noteChanged || progressChanged)
    ) {
      actionData.userActionProgress = progressValue; // ✅ 04 Sep 2025 Set here
      actionData.actualValue = Number(formData.actualValue); // ✅ 04 Sep 2025
      actionData.userActionNotes = [newNote];
  }

  //console.log('action Name:',actionData.userActionName)
  //console.log('action CompleteBy Date:',actionData.completeBy)

  if (formData.pic) {
    // Convert the image to a base64 string
    const reader = new FileReader()
    reader.onloadend = async () => {
      // Once conversion is done, add the base64 string to your data object
      actionData.pic = reader.result

      // Now, send the data object to the server
      try {
        actionData.id = id
        // console.log("Form submitted.")
        await updateUserAction(actionData).unwrap() // Dispatch the RTK Query mutation
      } catch (error) {
        console.error(error)
      }
    }
    reader.readAsDataURL(formData.pic)
  } else {
    // No image, send other data as is
    try {
      actionData.id = id
      // console.log("Form submitted.")
      await updateUserAction(actionData).unwrap() // Dispatch the RTK Query mutation
    } catch (error) {
      console.error(error)
    }
  }
}
  
// 9 - Handle Cancel 
  const handleCancel = () => {
    // navigate(triggeringUrl)
    if (onClose) onClose()
  }

// 10 - Handle Clear
  const handleClear = () => {
    setFormData({
      userId: "",
      trailId: "", 
      sessionId: "",
      actionTypeId: "",
      userActionName: "",
      userActionDesc: "",
      completeBy: "", 
      aboutMe: "",
      pic: "" ,
      // 01 Sep 2025
      actionRecurrencePattern: "",
      recurrenceStartDate: "",
      recurrenceEndDate: "",
      weekdayOnly: false,
      //03 Sep 2025
      actionUoM: "",
      //04 Sep 2025
      targetValue: 0,
    })
  }

    return (
      <div className="bg-gray-200 p-1 mx-auto mt-8 rounded-lg shadow-md shadow-black z-80 font-poppins">
        {isLoading && <PulseLoader color={"#ea580c"} />}
        {isError && (
          <p className="text-orange-700 font-semi-bold py-2">
            {error?.data?.message}
          </p>
        )}
        <div className="bg-white p-1 m-1 rounded">
          <div>
          <div className="flex justify-start items-center">
              <div
              className="flex justify-center items-center cursor-pointer rounded-full h-5 w-5 hover:bg-white"
              onClick={() => setShowMainSection(prev => !prev)}
            >
              {showMainSection ? (
                <MinusIcon className="h-4 w-4 text-orange-600" />
              ) : (
                <PlusIcon className="h-4 w-4 text-orange-600" />
              )}
            </div>
            <p className="p-1 m-1 font-bold text-xl">Update an Action</p>
            
          </div>
            { !showMainSection && <p className="px-1 mx-1 text-xs italic text-orange-600">Click the + button to update Action Details</p>}
          </div>
          
  
          <div className="bg-gray-200 rounded m-1 p-1">
            <form
              onSubmit={handleSubmit}
              className="m-1 p-1"
            >

              {showMainSection && (
                <>

                  <div className="m-1 p-1 bg-white rounded">
                    <div className="grid grid-cols-6 justify-start items-center m-2 pb-2 border-b">
                      <p className="col-span-1 font-bold text-orange-800 mx-1 px-1">Trail</p>
                      <p className="col-span-5 text-gray-800 mx-1 px-1">{formData.trailName}</p>
                    </div>
                    {formData.selectedFocus && (
                    <div className="grid grid-cols-6 justify-start items-center m-2 pb-2 border-b">
                      <p className="col-span-1 font-bold text-orange-800 mx-1 px-1">Focus</p>
                      <p className="col-span-5 text-gray-800 mx-1 px-1">{formData.selectedFocus}</p>
                    </div>)}
                    {formData.visionName && (
                    <div className="grid grid-cols-6 justify-start items-center m-2 pb-2 border-b">
                      <p className="col-span-1 font-bold text-orange-800 mx-1 px-1">Vision</p>
                      <p className="col-span-5 text-gray-800 mx-1 px-1">{formData.visionName}</p>
                    </div>)}
                    {formData.missionName && (
                    <div className="grid grid-cols-6 justify-start items-center m-2 pb-2 border-b">
                      <p className="col-span-1 font-bold text-orange-800 mx-1 px-1">Mission</p>
                      <p className="col-span-5 text-gray-800 mx-1 px-1">{formData.missionName}</p>
                    </div>)}
                    {formData.planName && (
                    <div className="grid grid-cols-6 justify-start items-center m-2 pb-2 border-b">
                      <p className="col-span-1 font-bold text-orange-800 mx-1 px-1">Plan</p>
                      <p className="col-span-5 text-gray-800 mx-1 px-1">{formData.planName}</p>
                    </div>)}
                    {formData.goalName && (
                    <div className="grid grid-cols-6 justify-start items-center m-2 pb-2 border-b">
                      <p className="col-span-1 font-bold text-orange-800 mx-1 px-1">Goal</p>
                      <p className="col-span-5 text-gray-800 mx-1 px-1">{formData.goalName}</p>
                    </div>)}
                    {formData.goalPeriodName && (
                    <div className="grid grid-cols-6 justify-start items-center m-2 pb-2 border-b">
                      <p className="col-span-1 font-bold text-orange-800 mx-1 px-1">Goal Period</p>
                      <p className="col-span-5 text-gray-800 mx-1 px-1">{formData.goalPeriodName}</p>
                    </div>)}
                  </div>

                  <div id="actionTypeSelector" className="mt-2 pt-2">
                    <p className="font-bold text-orange-800 mb-2 mx-1 px-1">Action Type</p>
                    <select
                      name="actionTypeId"
                      id="actionTypeId"
                      value={formData?.actionTypeId}
                      onChange={handleChange}
                      className="w-full rounded p-2 m-1"
                    >
                      {actionTypeOptions}
                    </select>
                  </div>

                  {userRoles.includes("OrgSponsor") && (
                    <>    
                      {/* 12 Dec 2025 - NEW — Checkbox to show/hide assignment UI */}
                      <div className="flex items-center gap-2 mx-1 px-1 mt-2">
                        <input
                          type="checkbox"
                          id="toggleAssign"
                          checked={showAssignUI}
                          onChange={() => setShowAssignUI(prev => !prev)}
                          className="h-4 w-4 text-orange-600"
                        />
                        <label htmlFor="toggleAssign" className="font-bold text-orange-800">
                          Update Assign To?
                        </label>
                      </div>
                      
                      
                      {/* 12 Dec 2025 - Render UI only if checkbox is ON */}
                      {showAssignUI && (
                        <div className="bg-white rounded m-2 p-3 border">
                            {/* 07 Sep 2025 - Assign Users */}
                          <div>
                            {/* 1️⃣ Label */}
                            <p className="font-bold text-orange-800 mt-2 pt-2 mx-1 px-1">Assign To</p>

                            {/* 2️⃣ Badge-style chip selector */}
                            <div className="flex flex-wrap gap-2 mb-4 bg-white rounded m-1 p-2">
                              {usersData?.ids
                                .map((id) => usersData.entities[id])
                                .filter((user) => user._id !== userId && user.userOrgId?._id === userOrgId)
                                .map((user) => {
                                  // const isSelected = assignedUsers.includes(user._id);

                                  // 12 Dec 2025 - Users assigned at creation CANNOT be removed; new ones CAN toggle

                                  const previouslyAssigned = (userAction.assignedUsers || []).some(
                                    u => u.userId === user._id
                                  );

                                  const isSelected = assignedUsers.some(u => u.userId === user._id);
                                  const isDisabled = previouslyAssigned; // 12 Dec 2025 - Disable toggle for previously assigned users
                                  
                                  // const isDisabled = isSelected; // Disable toggle for already assigned users
                                  
                                  
                                  
                                  
                                  return (
                                    <button
                                      key={user._id}
                                      type="button"
                                      // 12 Dec 2025
                                      onClick={() => {
                                        if (isDisabled) return; // cannot remove existing assignments

                                        if (isSelected) {
                                          // toggle OFF only for NEW assignments
                                          setAssignedUsers(prev => prev.filter(u => u.userId !== user._id));
                                        } else {
                                          // toggle ON for unassigned users
                                          setAssignedUsers(prev => [...prev, { userId: user._id, assignmentType }]);
                                        }
                                      }}
                                      // onClick={() => {
                                      //   if (!isDisabled) {
                                      //     if (isSelected) {
                                      //       setAssignedUsers(prev => prev.filter(u => u.userId !== user._id));
                                      //     } else {
                                      //       setAssignedUsers(prev => [...prev, { userId: user._id, assignmentType }]);
                                      //     }
                                      //   }
                                      // }}
                                      disabled={isDisabled}
                                      className={`px-3 py-1 rounded-full text-sm border font-medium transition 
                                        ${
                                          isSelected
                                            ? "bg-orange-500 text-white border-orange-500"
                                            : "bg-white text-gray-700 border-gray-300 hover:bg-orange-100"}
                                        ${isDisabled ? "cursor-not-allowed opacity-60" : ""}`}
                                    >
                                      {user.name} {user.userOrgRole ? `- ${user.userOrgRole.orgRoleName}` : ''}
                                    </button>
                                  );
                                })}
                            </div>
                          </div>

                          {/* 🔸 08 Sep 2025 – Toggle for Assignment Type */}
                          <p className="font-bold text-orange-800 mt-2 pt-2 mx-1 px-1">Assignment Type</p>
                          <div className="flex space-x-2 mx-1 px-1">
                            {["Delegated", "Collective"].map((type) => (
                              <button
                                key={type}
                                type="button"
                                className={`px-3 py-1 rounded-full text-sm font-semibold border transition 
                                  ${assignmentType === type
                                    ? "bg-orange-500 text-white border-orange-600"
                                    : "bg-white text-gray-800 border-gray-300 hover:bg-orange-100"}`}
                                onClick={() => setAssignmentType(type)}
                              >
                                {type}
                              </button>
                            ))}
                          </div>
                        </div>
                        
                      )}
                      
                      


                    </>
                  )}

      
                  <div id="userActionName" className="mt-2 pt-2">
                    <p className="font-bold text-orange-800 mb-2 mx-1 px-1">Action Name</p>
                    <input
                      type="text"
                      name="userActionName"
                      placeholder="Enter the userAction name here"
                      value={formData?.userActionName}
                      onChange={handleChange}
                      className="w-full rounded p-2 m-1"
                    />
                  </div>
        
                  <div id="userActionDesc" className="mt-2 pt-2">
                    <p className="font-bold text-orange-800 mb-2 mx-1 px-1">Action Description</p>
                    <div className="bg-white w-full rounded p-2 m-1 font-poppins">
                    <EtContentEditorLightWithPlace
                      value={formData?.userActionDesc}
                      handleChange={(content) =>
                        setFormData({ ...formData, userActionDesc: content })
                      }
                      placeholderText={
                        formData.userActionDesc
                      }
                    />
                    </div>
                  </div>

                  {/* 03 Sep 2025 - actionUoM */}
                  <div className="flex justify-between items-center mb-2">
                    {/* 04 Sep 2025 -  Target Value */}
                      <div id="targetValue" className="mt-2 pt-2 w-full mr-1 pr-1">
                        <p className="font-bold text-orange-800 mb-2 mx-1 px-1">Target Value</p>
                        <input
                          type="number"
                          min="0"
                          value={formData.targetValue || 0}
                          onChange={(e) => setFormData(prev => ({ ...prev, targetValue: e.target.value }))}
                          className="w-full rounded p-2 m-1"
                        />
                      </div>

                    <div id="actionUoM" className="mt-2 pt-2 w-full pr-1 mr-1">
                      <p className="font-bold text-orange-800 mb-2 mx-1 px-1">Unit of Measure (UoM)</p>
                      <input
                        type="text"
                        name="actionUoM"
                        placeholder="e.g., messages sent, leads contacted"
                        value={formData?.actionUoM}
                        onChange={handleChange}
                        className="w-full rounded p-2 m-1"
                      />
                    </div>

                    
                    </div>


                  <div id="completeBy" className="flex justify-between items-center">
                      <div className="p-2 m-1 bg-white rounded w-full"><p className="font-bold text-orange-800">Complete By:</p></div>
                      <input
                          type="date"
                          name="completeBy"
                          value={formData.completeBy || ""} // 07 Dec 2025
                          // value={formatDateISO(formData?.completeBy)}
                          // value={
                          //   formData?.completeBy?.includes('T')
                          //   ? formatUTCToLocalDateInput(formData.completeBy)
                          //   : formData.completeBy
                          // }
                          onChange={handleChange}
                          className="w-full rounded p-2 m-1"
                      />
                  </div>

                  {/* 2️⃣ Recurrence Pattern (disabled) */}
                  <p className="font-bold text-orange-800 mt-2 pt-2 mx-1 px-1">Recurrence Pattern</p>
                  <div className="flex flex-wrap gap-2 mx-1 px-1">
                    <button
                      type="button"
                      className="px-3 py-1 rounded-full border border-gray-300 bg-gray-100 text-gray-600 cursor-not-allowed"
                      disabled
                    >
                      {formData.actionRecurrencePattern}
                    </button>
                  </div>

                  {/* 3️⃣ Recurrence Date Fields (Editable with Conditions) */}
                  {formData.actionRecurrencePattern !== "OneTime" && (
                    <div className="grid grid-cols-2 gap-4 my-2">
                      {/* Recurrence Start Date */}
                      <div>
                        <p className="font-bold text-orange-800 mt-2 pt-2 mx-1 px-1">Recurrence Start Date</p>
                        <input
                          type="date"
                          name="recurrenceStartDate"
                          value={formData.recurrenceStartDate || ""} // 07 Dec 2025
                          // value={
                          //   formData?.recurrenceStartDate?.includes('T')
                          //   ? formatUTCToLocalDateInput(formData.recurrenceStartDate)
                          //   : formData.recurrenceStartDate
                          // }
                          onChange={handleChange}
                          disabled={!allowChangeStart}
                          className={`w-full rounded border px-2 py-1 ${
                            allowChangeStart ? 'bg-white' : 'bg-gray-100 text-gray-500 cursor-not-allowed'
                          }`}
                        />
                        {!allowChangeStart && (
                          <p className="text-xs text-gray-500 mt-1 italic">
                            Cannot update start date as it is in the past or logs have already been created.
                          </p>
                        )}
                      </div>

                      {/* Recurrence End Date */}
                      <div>
                        <p className="font-bold text-orange-800 mt-2 pt-2 mx-1 px-1">Recurrence End Date</p>
                        <input
                          type="date"
                          name="recurrenceEndDate"
                          value={formData.recurrenceEndDate || ""} // 07 Dec 2025
                          // value={
                          //   formData?.recurrenceEndDate?.includes('T')
                          //   ? formatUTCToLocalDateInput(formData.recurrenceEndDate)
                          //   : formData.recurrenceEndDate
                          // }
                          onChange={handleChange}
                          disabled={!allowChangeEnd}
                          className={`w-full rounded border px-2 py-1 ${
                            allowChangeEnd ? 'bg-white' : 'bg-gray-100 text-gray-500 cursor-not-allowed'
                          }`}
                        />
                        {!allowChangeEnd && (
                          <p className="text-xs text-gray-500 mt-1 italic">
                            Cannot update end date as logs already exist for the end date or it is in the past.
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* 4️⃣ Weekday Only Toggle (Daily recurrence only) */}
                  {formData.actionRecurrencePattern === "Daily" && (
                    <div className="mt-4">
                      <p className="font-bold text-orange-800 mx-1 px-1">Weekday Only (Mon–Fri)</p>
                      <button
                        type="button"
                        onClick={() =>
                          setFormData(prev => ({ ...prev, weekdayOnly: !prev.weekdayOnly }))
                        }
                        disabled={!allowToggleWeekdayOnly}
                        className={`mt-1 px-4 py-2 rounded-full ${
                          formData.weekdayOnly ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-500'
                        } ${!allowToggleWeekdayOnly && 'opacity-50 cursor-not-allowed'}`}
                      >
                        {formData.weekdayOnly ? 'Yes' : 'No'}
                      </button>
                      {!allowToggleWeekdayOnly && (
                        <p className="text-xs text-gray-500 mt-1 italic">
                          {/* Cannot toggle as recurrence dates are locked or pattern is not Daily. */}
                          Cannot toggle as recurrence dates are locked.
                        </p>
                      )}
                    </div>
                  )}

                </>
              )}
              
              {/* Progress Note Textarea */}
              <div>

                {/* 03 Sep 2025 - Recurring Action Log */}
                {/* {formData.actionRecurrencePattern !== "OneTime" && currentLog && (
                  <div className="my-4 p-3 rounded bg-gray-50 border border-gray-200">
                    <p className="text-orange-800 font-bold mb-2">Log Recurrence Update</p>

                    {/* Action - Key attributes for reference - 04 Sep 2025 
                    <div className="bg-orange-50 rounded my-2">
                      <div className="flex justify-start items-center mt-2 pt-2 border-t">    
                        <p className="mx-2 px-2 font-bold text-gray-800">Action: </p>
                        <p className="mx-1 px-1">{formData?.userActionName}</p>
                      </div>

                      {/* 19 Sep 2025 - Action Goal Role - Enabler or Outcome *
                      <div className="flex justify-start items-center mt-2 pt-2 border-t">    
                        <p className="mx-2 px-2 font-bold text-gray-800">Action Role: </p>
                        <p className="mx-1 px-1">{formData?.actionGoalRole}</p>
                      </div>
                      
                      <div className="flex justify-start items-center m-1 p-1 pb-2">
                        <div className="flex justify-start items-center">
                          <p className="mx-1 px-1 text-gray-800 font-bold">Recurrence  : </p>
                          <p>{formData?.actionRecurrencePattern}</p>
                        </div>
                        <div className="flex justify-start items-center">
                          <p className="mx-1 px-1 text-gray-800 font-bold">Log For  : </p>
                          <p className="mx-1">{moment(currentLog.logDate).format('DD MMM YYYY')}</p>
                        </div>                       
                        <div className="flex justify-start items-center mx-1 px-1">
                          <p className="mx-1 text-gray-800 font-bold">Target  : </p>
                          <p>{formData?.targetValue}</p>
                          <p className="mx-1">{formData?.actionUoM}</p>
                        </div>                       
                      </div>
                    </div>

                    {/* Actual Value *
                    <div className="mb-2">
                      <label className="text-sm font-bold text-gray-700">Actual Value</label>
                      <input
                        type="number"
                        min="0"
                        value={formData.todayActualValue || 0}
                        onChange={(e) => setFormData(prev => ({ ...prev, todayActualValue: e.target.value }))}
                        className="w-full rounded p-2 mt-1 border border-gray-300"
                      />
                    </div>

                    {/* Note 
                    <div className="mb-2">
                      <p className="text-sm font-bold text-gray-700">Progress Note</p>
                      <textarea
                        rows="3"
                        value={formData.todayProgressNote || ""}
                        onChange={(e) => setFormData(prev => ({ ...prev, todayProgressNote: e.target.value }))}
                        className="w-full rounded p-2 mt-1 border border-gray-300"
                      />
                    </div>

                    {/* Progress *
                    <div className="w-full">
                        {/* Label *
                        <p className="text-sm font-bold text-gray-700 mb-1 block">Progress %</p>

                        {/* Custom slider with number input *
                        <div className="flex items-center space-x-3">
                          {/* Slider track + thumb *
                          <div className="relative w-full h-5">
                            {/* Base track *
                            <div className="absolute top-1/2 transform -translate-y-1/2 w-full h-2 rounded bg-gray-200">
                              <div
                                className="h-full bg-orange-600 rounded transition-all duration-300"
                                style={{ width: `${formData.todayProgress || 0}%` }}
                              ></div>
                            </div>

                            {/* Slider input (thumb only) *
                            <input
                              type="range"
                              min="0"
                              max="100"
                              step="1"
                              value={formData.todayProgress || 0}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  todayProgress: Number(e.target.value),
                                }))
                              }
                              className={`
                                absolute top-0 left-0 w-full h-5 appearance-none bg-transparent z-10
                                [&::-webkit-slider-thumb]:appearance-none
                                [&::-webkit-slider-thumb]:h-4
                                [&::-webkit-slider-thumb]:w-4
                                [&::-webkit-slider-thumb]:rounded-full
                                [&::-webkit-slider-thumb]:bg-orange-600
                                [&::-webkit-slider-thumb]:border
                                [&::-webkit-slider-thumb]:border-gray-300
                                [&::-webkit-slider-thumb]:cursor-pointer
                                [&::-webkit-slider-thumb]:mt-[-1px] //aligns thumb with track center

                                [&::-moz-range-thumb]:appearance-none
                                [&::-moz-range-thumb]:h-4
                                [&::-moz-range-thumb]:w-4
                                [&::-moz-range-thumb]:rounded-full
                                [&::-moz-range-thumb]:bg-orange-600
                                [&::-moz-range-thumb]:border
                                [&::-moz-range-thumb]:border-gray-300
                                [&::-moz-range-thumb]:cursor-pointer
                              `}
                            />
                          </div>

                          {/* Numeric input box *
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={formData.todayProgress || 0}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                todayProgress: Number(e.target.value),
                              }))
                            }
                            className="w-16 border border-gray-300 rounded p-1 text-center text-sm"
                          />
                        </div>
                    </div>

                  </div>
                )} */}

                {/* 04 Sep 2025 - display only for "OneTime - else populate from the progress info from each recurrence log" */}
                {/* {formData?.actionRecurrencePattern === "OneTime" && ( */}
                   <div>
                     {/* <p className="m-1 p-1 font-bold text-lg text-orange-800">Update Action Progress</p> */}

                     {/* Action - Key attributes for reference - 04 Sep 2025 */}
                     {/* <div className="bg-orange-50 rounded my-2"> */}
                       {/* <div className="flex justify-start items-center mt-2 pt-2 border-t">    
                  //       <p className="mx-2 px-2 font-bold text-gray-800">Action: </p>
                  //       <p className="mx-1 px-1">{formData?.userActionName}</p>
                  //     </div> */}

                       {/* 19 Sep 2025 - Action Goal Role - Enabler or Outcome */}
                       {/* <div className="flex justify-start items-center mt-2 pt-2 border-t">    
                  //       <p className="mx-2 px-2 font-bold text-gray-800">Action Role: </p>
                  //       <p className="mx-1 px-1">{formData?.actionGoalRole}</p>
                  //     </div>
                      
                  //     <div className="flex justify-start items-center m-1 p-1 pb-2">
                  //       <div className="flex justify-start items-center">
                  //         <p className="mx-1 px-1 text-gray-800 font-bold">Recurrence  : </p>
                  //         <p>{formData?.actionRecurrencePattern}</p>
                  //       </div>
                  //       <div className="flex justify-start items-center">
                  //         <p className="mx-1 px-1 text-gray-800 font-bold">Log For  : </p>
                  //         <p className="mx-1">{moment().format('DD MMM YYYY')}</p>
                  //       </div>                       
                  //       <div className="flex justify-start items-center mx-1 px-1">
                  //         <p className="mx-1 text-gray-800 font-bold">Target  : </p>
                  //         <p>{formData?.targetValue}</p>
                          
                  //       </div>                       
                  //       <div className="flex justify-start items-center mx-1 px-1">
                  //         <p className="mx-1 text-gray-800 font-bold">UoM  : </p>
                          
                  //         <p className="mx-1">{formData?.actionUoM}</p>
                  //       </div>                       
                  //     </div> */}
                     {/* </div> */}


                     {/* Actual Value - Non recurring Actions - 04 Sep 2025 */}
                     {/* <div className="mb-2">
                  //     <label className="text-sm font-bold text-gray-700">Actual Value</label>
                  //     <input
                  //       type="number"
                  //       min="0"
                  //       value={formData.actualValue || 0}
                  //       onChange={(e) => setFormData(prev => ({ ...prev, actualValue: e.target.value }))}
                  //       className="w-full rounded p-2 mt-1 border border-gray-300"
                  //     />
                  //   </div> */}


                     {/* <div className='flex justify-between items-center'> */}
                       {/* <textarea
                  //       id="actionProgress-textarea"
                  //       value={progressNoteText}
                  //       onChange={(e) => setProgressNoteText(e.target.value)}
                  //       placeholder="Enter progress update..."
                  //       rows={3}
                  //       className="w-full border border-gray-300 rounded p-7 pr-20 pl-4 resize-y overflow-auto focus:ring-1 focus:ring-orange-500 text-xs"
                  //     /> */}
                       {/* Progress Slider & Number Input */}
                       {/* <div className="flex items-center my-2 w-full">
                  //       <p className='text-xs text-gray-400 mx-1 px-1 text-nowrap'>Progress (%) : </p>
                  //       <input
                  //         type="range"
                  //         min="0"
                  //         max="100"
                  //         step="1"
                  //         value={progressValue}
                  //         onChange={(e) => setProgressValue(Number(e.target.value))}
                  //         className="w-full"
                  //       />
                  //       <input
                  //         type="number"
                  //         min="0"
                  //         max="100"
                  //         value={progressValue}
                  //         onChange={(e) => setProgressValue(Number(e.target.value))}
                  //         className="ml-2 border border-gray-300 rounded p-1 text-center"
                  //       />
                  //     </div> */}
                     {/* </div> */}
                   </div>
                {/* )} */}
                
              </div>

              
  
              {/* <div id="aboutMe">
                <input
                  type="text"
                  name="aboutMe"
                  placeholder="Add something about the personality of this action"
                  value={formData.aboutMe}
                  onChange={handleChange}
                  className="w-full rounded p-2 m-1"
                />
              </div>
  
              <div id="pic">
                <input
                  type="file"
                  name="pic"
                  onChange={handleChange}
                  className="w-full rounded p-2 m-1"
                />
              </div> */}
  
  
              <div id="actions" className="bg-white my-1 px-1 py-3 rounded">
                <EtBtnFormBasic
                  name={"Update"}
                  type={"create"}
                  handleClick={handleSubmit}
                />
                <EtBtnFormBasic
                  name={"Clear"}
                  type={"clear"}
                  handleClick={handleClear}
                />
                <EtBtnFormBasic
                  name={"Cancel"}
                  type={"cancel"}
                  handleClick={handleCancel}
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    )

}

export default EtUpdateUserActionComp